import { Button, Checkbox, Input } from "component-library";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Logo } from "../components/logo/logo";
import { useAuth } from "../components/auth/AuthenticationContext";

interface LoginFormData {
  username: string;
  password: string;
  rememberMe: boolean;
}

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    defaultValues: {
      rememberMe: false,
    },
  });

  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (data: LoginFormData) => {
    setError(null);

    login(data.username, data.password, data.rememberMe).catch((err) => {
      if (err.response && err.response.status === 401) {
        setError(t("login.invalidCredentials"));
        return;
      }
      setError(t("login.unexpectedError"));
    });
  };

  return (
    <div
      style={{
        background:
          "linear-gradient(135deg, #003A73 0%, #357ABD 50%, #0078AB 100%)",
      }}
      className="min-h-screen flex items-center justify-center p-4 relative"
    >
      <Logo variant="light" className="absolute top-4 left-4" />
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl px-4 pt-6 pb-10">
        <div className="text-xl text-text-primary mb-6 text-center">
          {t("login.title")}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="text-red-800 text-sm text-center">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
          <div>
            <Input
              type="text"
              placeholder={t("login.username.placeholder")}
              {...register("username", {
                required: t("login.username.required"),
              })}
            />
            {errors.username && (
              <div className="text-red-500 text-sm mt-1">
                {errors.username.message}
              </div>
            )}
          </div>
          <div>
            <Input
              type="password"
              placeholder={t("login.password.placeholder")}
              {...register("password", {
                required: t("login.password.required"),
              })}
            />
            {errors.password && (
              <div className="text-red-500 text-sm mt-1">
                {errors.password.message}
              </div>
            )}
          </div>
          <Controller
            name="rememberMe"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox className="mt-3" value={value} onChange={onChange}>
                <span className="text-sm -ml-2 font-normal">
                  {t("login.rememberMe")}
                </span>
              </Checkbox>
            )}
          />
          <Button
            type="submit"
            className="w-full mt-6"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? t("login.submitting") : t("login.submit")}
          </Button>
        </form>
      </div>
    </div>
  );
};
